// The marks a field of the Settings form carries — t-159 §1 (the legend strip
// at 781-788, the badges beside every `api-name`) and §4 (the "Scheduled: …"
// line under a control the package will overwrite).

import { Clock, Lock, Zap } from "lucide-react";

import { pendingValueOf, usePendingMarks } from "./pendingContext";
import {
  formatFieldValue,
  whenBadge,
  WHEN_LABEL,
  WHEN_LEGEND_LABEL,
  type WhenBadge as WhenBadgeKind,
} from "./pendingFields";
import styles from "./pending.module.css";

function badgeIcon(kind: WhenBadgeKind) {
  if (kind === "lock") return <Lock size={11} aria-hidden />;
  if (kind === "now") return <Zap size={11} aria-hidden />;
  return <Clock size={12} aria-hidden />;
}

/**
 * When this field may move, as the small outlined pill the mockup pins to the
 * right of the api name. Renders nothing at all for the fields that go either
 * way — which is most of them, and the reason the three that don't are worth
 * marking.
 */
export function WhenBadge({
  field,
  /** The legend's shorter wording ("next cycle" rather than "next cycle only"),
     used only in the strip that explains the three marks. */
  legend,
}: {
  field: string;
  legend?: boolean;
}) {
  const { marked, cuts } = usePendingMarks();
  const kind = whenBadge(field);
  if (!kind || (!marked && !legend)) return null;
  // A promise about a cut, on a board that has none. The legend keeps it —
  // there it explains the grammar rather than describing this board.
  if (kind === "cut" && !cuts && !legend) return null;
  const label = legend ? WHEN_LEGEND_LABEL[kind] : WHEN_LABEL[kind];
  return (
    <span className={`${styles.whenBadge} ${styles[kind]}`}>
      {badgeIcon(kind)}
      {label}
    </span>
  );
}

/**
 * "Scheduled: `sum`" — what the next cut will write into the control above.
 * The control keeps showing the board's own value, because that is what the
 * board is doing right now; this line is the only place the other answer is.
 */
export function PendingNote({ field }: { field: string }) {
  const marks = usePendingMarks();
  const value = pendingValueOf(marks, field);
  if (value === undefined) return null;
  return (
    <div className={styles.pendingNote}>
      <Clock size={12} aria-hidden />
      Scheduled: <code>{formatFieldValue(field, value)}</code>
    </div>
  );
}

/**
 * The strip above the form that says what a mark on a field means. Only three
 * marks exist, and two of them name a single field each — which is the point
 * it is making: everything a board is made of moves now, at the next cut.
 */
export function LegendStrip() {
  return (
    <div className={styles.legendStrip}>
      <span className={styles.legendIco}>
        <Clock size={16} aria-hidden />
      </span>
      <span>
        <b>What a mark on a field means.</b>{" "}
        <WhenBadge field="window_kind" legend /> — the cycles already cut belong
        to the old value, so the field moves where a cycle has no history yet.
        Everything a board is made of is in this group now,{" "}
        <b>including what used to be settable only at creation</b>.{" "}
        <WhenBadge field="status" legend /> — <code>status</code> alone.{" "}
        <WhenBadge field="ext_key" legend /> — <code>ext_key</code> alone, it
        lives in builds already on players&apos; phones. Everything unmarked
        goes either way, and the save bar is where you choose.
      </span>
    </div>
  );
}
